"use client";

import { useState, useEffect } from 'react';
import { Edit2, Save, X, Check } from 'lucide-react';
import { BackendAuth } from '@/lib/auth-backend';

/**
 * Inline Translation Editor
 * Wraps translated text and allows admins to edit translations directly on the page
 *
 * Usage:
 * import { TranslationEdit } from '@/components/TranslationEditWrapper';
 *
 * <TranslationEdit translationKey="developer.title" language="de">
 *   {t('developer.title')}
 * </TranslationEdit>
 */
export function TranslationEdit({
  translationKey,
  language = 'de',
  namespace = 'translation',
  children,
  className = ''
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [originalValue, setOriginalValue] = useState('');
  const [currentValue, setCurrentValue] = useState('');

  // Check if user has translation edit permission (Admins only: menu_group_id = 1)
  useEffect(() => {
    const checkPermission = () => {
      try {
        const user = BackendAuth.getUser();
        if (!user) {
          setHasPermission(false);
          return;
        }

        // Only admins (menu_group_id = 1) can edit translations
        const isAdmin = user.menu_group_id === 1;
        setHasPermission(isAdmin);
      } catch (error) {
        console.error('Failed to check translation permission:', error);
        setHasPermission(false);
      }
    };

    checkPermission();
  }, [language]);

  // Initialize currentValue from children
  useEffect(() => {
    const textContent = typeof children === 'string'
      ? children
      : children?.props?.children || '';
    if (!currentValue) {
      setCurrentValue(textContent);
    }
  }, [children, currentValue]);

  const handleStartEdit = () => {
    // Use currentValue as the source
    const textContent = currentValue || (typeof children === 'string'
      ? children
      : children?.props?.children || '');

    setOriginalValue(textContent);
    setEditValue(textContent);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSave = async () => {
    if (!editValue.trim() || editValue === originalValue) {
      handleCancel();
      return;
    }

    setSaving(true);
    try {
      const response = await BackendAuth.apiCallJson('/api/translations/create', {
        method: 'POST',
        body: JSON.stringify({
          key: translationKey,
          language: language,
          value: editValue.trim(),
          namespace: namespace,
          context: 'Manually edited via inline editor'
        })
      });

      if (response?.success) {
        // Update the displayed value without reload
        setCurrentValue(editValue.trim());
        setIsEditing(false);
        setEditValue('');
      } else {
        alert('Failed to save translation. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save translation:', error);
      alert('Failed to save translation: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // If no permission, render children as-is
  if (!hasPermission) {
    return <>{children}</>;
  }

  // Editing mode
  if (isEditing) {
    return (
      <div className="inline-flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded border border-yellow-300 dark:border-yellow-700">
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm min-w-[200px] text-gray-900 dark:text-gray-100"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          disabled={saving}
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
          title="Save (Enter)"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
        </button>
        <button
          onClick={handleCancel}
          disabled={saving}
          className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          title="Cancel (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // View mode with edit indicator
  return (
    <span
      className={`inline-flex items-center gap-1 group relative ${className}`}
      style={{ cursor: 'pointer' }}
    >
      <span className="relative">
        {currentValue || children}
        <span
          className="absolute -bottom-0.5 left-0 right-0 h-px bg-blue-400 opacity-0 group-hover:opacity-50 transition-opacity"
          aria-hidden="true"
        />
      </span>
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleStartEdit();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleStartEdit();
          }
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
        title={`Edit translation: ${translationKey} (${language})`}
      >
        <Edit2 className="w-3 h-3" />
      </span>
    </span>
  );
}

/**
 * Batch Translation Editor Modal
 * For editing multiple translations at once
 */
export function TranslationBatchEditor({ translations = [], language = 'de', onClose }) {
  const [editedValues, setEditedValues] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(editedValues).map(([key, value]) => ({
        key,
        language,
        value,
        namespace: 'translation'
      }));

      for (const update of updates) {
        await BackendAuth.apiCallJson('/api/translations/create', {
          method: 'POST',
          body: JSON.stringify(update)
        });
      }

      window.location.reload();
    } catch (error) {
      console.error('Failed to save translations:', error);
      alert('Failed to save translations: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Batch Translation Editor
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {translations.map(({ key, value }) => (
            <div key={key} className="space-y-1">
              <label className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {key}
              </label>
              <input
                type="text"
                defaultValue={value}
                onChange={(e) => handleChange(key, e.target.value)}
                className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save All
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TranslationEdit;
